import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { FC, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { InputField } from '../../../../components/input-field';
import { AssetFieldInput } from '../../../../components/asset-field-input';
import { Item } from '../../../../types/item';
import { useAuth } from '../../../../hooks/use-auth';
import { useAxios } from '../../../../hooks/use-axios';
import { ResponseData } from '../../../../types/axios';
import {
  applyTitleMask,
  dynamicFields,
  getAssetConfig,
  getDetail,
  getDetails,
  getIdentifierField,
  itemOnlyFields,
} from '../../../../config/asset-config';

interface ItemEditDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
}

export const ItemEditDetailsDialog: FC<ItemEditDetailsDialogProps> = ({
  open,
  onClose,
  item,
  ...other
}) => {
  const queryClient = useQueryClient();
  const { axios } = useAxios();
  const { tenant } = useAuth();
  const config = useMemo(() => getAssetConfig(tenant), [tenant]);

  // With a SKU, only the item's own (itemOnly) fields are editable — the SKU's catalog fields are
  // shown read-only elsewhere. A SKU-less item holds (and edits) all its dynamic fields.
  const editableFields = useMemo(
    () => (item.sku ? itemOnlyFields(config) : dynamicFields(config)),
    [config, item.sku],
  );
  const identifierField = useMemo(() => getIdentifierField(config), [config]);
  const mask = config?.itemTitleMask;
  // The title is derived from the mask (from the serial / fingerprint number) when the item has a
  // SKU and a mask; otherwise it's free-text (SKU-less or unmasked tenants).
  const derivedTitleMode = Boolean(item.sku && mask);

  // Build the item title from the mask: serial -> "#<serial>", else the item's known fingerprint
  // number as "FN<zero-padded>". The edit endpoint does not fill the {{seq}} placeholder, so the
  // number must be resolved here (from the already-assigned skuSequence).
  const buildTitle = (details: Record<string, any>): string => {
    const serial = identifierField ? String(details?.[identifierField.id] ?? '').trim() : '';
    const seq = (item.publicMetadata as { skuSequence?: number })?.skuSequence;
    const counter = serial ? `#${serial}` : `FN${seq != null ? String(seq).padStart(3, '0') : ''}`;
    return applyTitleMask(config, item.sku, counter) ?? item.title;
  };

  const updateMutation = useMutation(async (updates: Partial<Item>) =>
    // The item edit endpoint derives the tenant from the item and merges publicMetadata.
    axios.put<ResponseData<Item>>(`/items/${item.id}`, updates),
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: item.title || '',
      details: Object.fromEntries(
        editableFields.map((f) => [f.id, getDetail(item, f.id) ?? '']),
      ) as Record<string, any>,
      submit: null,
    },
    validationSchema: Yup.object().shape({
      title: derivedTitleMode
        ? Yup.string()
        : Yup.string().max(255).required('Title is required'),
      details: Yup.object().shape(
        Object.fromEntries(
          editableFields.map((f) => {
            let validator = Yup.string();
            if (f.type === 'number') {
              validator = validator.matches(/^-?\d*\.?\d+$/, {
                message: `${f.label} must be a number`,
                excludeEmptyString: true,
              });
            }
            validator = f.required
              ? validator.required(`${f.label} is required`)
              : validator.optional();
            return [f.id, validator];
          }),
        ),
      ),
    }),
    onSubmit: (values, helpers) => {
      // Start from the item's existing details so any non-editable/legacy keys are preserved,
      // then overwrite the editable fields (coerce numbers; drop blanks — this clears a serial).
      const newDetails: Record<string, any> = { ...getDetails(item) };
      editableFields.forEach((field) => {
        const raw = values.details[field.id];
        if (raw === '' || raw === undefined || raw === null) {
          delete newDetails[field.id];
        } else {
          newDetails[field.id] = field.type === 'number' ? Number(raw) : raw;
        }
      });

      const updates: Partial<Item> = {
        title: derivedTitleMode ? buildTitle(values.details) : values.title,
        publicMetadata: { ...item.publicMetadata, details: newDetails },
      };

      updateMutation.mutate(updates, {
        onSuccess: () => {
          toast.success(`Item's properties were updated`);
          queryClient.invalidateQueries('item');
          onClose();
          helpers.setStatus({ success: true });
        },
        onError: (err: any) => {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err?.response?.data?.error?.message || err?.message });
        },
        onSettled: () => {
          helpers.setSubmitting(false);
        },
      });
    },
  });

  const derivedTitle = derivedTitleMode ? buildTitle(formik.values.details) : formik.values.title;

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: '100%' } }}
      TransitionProps={{ onExited: () => formik.resetForm() }}
      {...other}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Edit Item Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, gridAutoFlow: 'row', pt: 1 }}>
            {/* Title: derived from the mask (read-only) or free-text. */}
            <InputField
              disabled={derivedTitleMode}
              error={Boolean(formik.touched.title && formik.errors.title)}
              fullWidth
              helperText={
                derivedTitleMode
                  ? 'Derived from the serial / fingerprint number'
                  : formik.touched.title && formik.errors.title
              }
              label="Item Title"
              name="title"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={derivedTitle}
              required={!derivedTitleMode}
            />

            {editableFields.map((field) => (
              <AssetFieldInput
                key={field.id}
                field={field}
                name={`details.${field.id}`}
                value={formik.values.details[field.id] ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(
                  (formik.touched.details as any)?.[field.id] &&
                    (formik.errors.details as any)?.[field.id],
                )}
                helperText={
                  (formik.touched.details as any)?.[field.id] &&
                  (formik.errors.details as any)?.[field.id]
                }
              />
            ))}
          </Box>
          {formik.errors.submit && (
            <FormHelperText error sx={{ mt: 2 }}>
              {formik.errors.submit as string}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose} variant="text">
            Cancel
          </Button>
          <LoadingButton
            loading={formik.isSubmitting}
            color="primary"
            type="submit"
            size="large"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ItemEditDetailsDialog.defaultProps = {
  open: false,
};

ItemEditDetailsDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  // @ts-ignore
  item: PropTypes.any,
};
